import { supabase } from './supabaseClient';

export async function signUp({ email, password, name }) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { name } },
  });
  if (error) {
    console.error('Sign up error:', error);
    return { ok: false, error: error.message, code: error.status || error.code };
  }
  // Do NOT insert into profiles here; RLS may block before session is active.
  // Profile will be created via ensureProfile() after authentication completes.
  return { ok: true, user: data.user };
}

export async function signIn({ email, password }) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) {
    console.error('Sign in error:', error);
    return { ok: false, error: error.message, code: error.status || error.code };
  }
  return { ok: true, user: data.user };
}

export async function signInWithGoogle(redirectTo) {
  const { data, error } = await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo } });
  return error ? { ok: false, error: error.message } : { ok: true, url: data?.url };
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  return error ? { ok: false, error: error.message } : { ok: true };
}

export async function resendConfirmationEmail(email) {
  try {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
    });
    if (error) {
      console.error('Resend confirmation error:', error);
      return { ok: false, error: error.message, code: error.status || error.code };
    }
    return { ok: true };
  } catch (e) {
    console.error('Resend confirmation exception:', e);
    return { ok: false, error: String(e) };
  }
}

export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

export async function getUserProfile(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  return error ? null : data;
}

export async function ensureProfile(user) {
  // Check if profile exists
  const existing = await getUserProfile(user.id);
  if (existing) return existing;
  
  // Generate referral code for new user
  const referralCode = 'REF' + Math.random().toString(36).substring(2, 8).toUpperCase();
  
  // Create profile if it doesn't exist (for OAuth users or new email/password signups)
  const name = user.user_metadata?.full_name || user.user_metadata?.name || null;
  const { data, error } = await supabase
    .from('profiles')
    .insert({ 
      id: user.id, 
      email: user.email, 
      name: name || 'Developer',
      referral_code: referralCode 
    })
    .select()
    .single();
  
  if (error) {
    console.error('Profile creation error:', error);
    return null;
  }
  return data;
}

export async function getSession() {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

export async function getUserCount() {
  const { count, error } = await supabase
    .from('profiles')
    .select('*', { count: 'exact', head: true });
  return error ? 0 : (count || 0);
}

export async function addToWaitlist({ email, painPoints }) {
  // Check if email already exists
  const { data: existing } = await supabase
    .from('waitlist')
    .select('*')
    .eq('email', email)
    .single();
  
  if (existing) {
    return { ok: false, error: 'Email already on waitlist', position: existing.position };
  }
  
  // Get current max position
  const { data: maxData } = await supabase
    .from('waitlist')
    .select('position', { count: 'exact' })
    .order('position', { ascending: false })
    .limit(1);
  
  const position = (maxData && maxData.length > 0) ? maxData[0].position + 1 : 1;
  
  // Insert new waitlist entry
  const { error } = await supabase
    .from('waitlist')
    .insert({ email, pain_points: painPoints, position });
  
  if (error) return { ok: false, error: error.message };
  return { ok: true, position };
}

export async function getWaitlistPosition(email) {
  const { data } = await supabase
    .from('waitlist')
    .select('position')
    .eq('email', email)
    .single();
  return data?.position || null;
}

// Forum functions
export async function createPost({ title, content, category = 'general', authorId, visibility = 'public', hashtags = [] }) {
  const { data, error } = await supabase
    .from('posts')
    .insert({ title, content, category, author_id: authorId, visibility })
    .select()
    .single();
  
  if (error) return { ok: false, error: error.message };
  
  // Link hashtags if provided
  if (hashtags.length > 0) {
    await linkPostToHashtags(data.id, hashtags);
  }
  
  return { ok: true, post: data };
}

export async function updatePost({ postId, title, content, category, visibility, hashtags = [] }) {
  const { data, error } = await supabase
    .from('posts')
    .update({ title, content, category, visibility })
    .eq('id', postId)
    .select()
    .single();
  
  if (error) return { ok: false, error: error.message };
  
  // Update hashtags if provided
  if (hashtags) {
    // Delete old hashtag links
    await supabase
      .from('post_hashtags')
      .delete()
      .eq('post_id', postId);
    
    // Add new hashtags
    if (hashtags.length > 0) {
      await linkPostToHashtags(postId, hashtags);
    }
  }
  
  return { ok: true, post: data };
}

export async function getPosts(limit = 20, offset = 0, currentUser = null) {
  const { data, error, count } = await supabase
    .from('posts')
    .select('*', { count: 'exact' })
    .order('comment_count', { ascending: false })
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);
  
  if (error) return { ok: false, posts: [], total: 0, error: error.message };
  
  // Filter by visibility: show public posts to everyone, private posts only to logged-in users
  // Posts without visibility field default to 'public'
  const filteredPosts = data?.filter(post => {
    const visibility = post.visibility || 'public'; // Default to public if undefined
    if (visibility === 'public') return true;
    if (visibility === 'private' && currentUser) return true;
    return false;
  }) || [];
  
  return { ok: true, posts: filteredPosts, total: count || 0 };
}

export async function getPostById(postId) {
  const { data, error } = await supabase
    .from('posts')
    .select('*')
    .eq('id', postId)
    .single();
  
  if (error) return null;
  return data;
}

export function canViewPost(post, currentUser) {
  // Public posts are visible to everyone
  if (post.visibility === 'public') return true;
  // Private posts are only visible to logged-in users
  if (post.visibility === 'private') return currentUser !== null;
  return false;
}

export async function getTopPosts(limit = 3, currentUser = null) {
  const { data, error } = await supabase
    .from('posts')
    .select('*')
    .order('comment_count', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(limit);
  
  if (error) return [];
  
  // Filter by visibility: show public posts to everyone, private posts only to logged-in users
  // Posts without visibility field default to 'public'
  return data?.filter(post => {
    const visibility = post.visibility || 'public'; // Default to public if undefined
    if (visibility === 'public') return true;
    if (visibility === 'private' && currentUser) return true;
    return false;
  }) || [];
}

export async function addComment({ postId, content, authorId }) {
  // Add comment
  const { data: comment, error: commentError } = await supabase
    .from('comments')
    .insert({ post_id: postId, content, author_id: authorId })
    .select()
    .single();
  
  if (commentError) return { ok: false, error: commentError.message };
  
  // Increment comment count on post
  const { error: updateError } = await supabase
    .from('posts')
    .update({ comment_count: await getPostCommentCount(postId) })
    .eq('id', postId);
  
  if (updateError) console.error('Error updating comment count:', updateError);
  
  return { ok: true, comment };
}

export async function getPostComments(postId) {
  const { data, error } = await supabase
    .from('comments')
    .select('*, profiles(name, email)')
    .eq('post_id', postId)
    .order('created_at', { ascending: true });
  
  if (error) return [];
  return data || [];
}

export async function getPostCommentCount(postId) {
  const { count, error } = await supabase
    .from('comments')
    .select('*', { count: 'exact', head: true })
    .eq('post_id', postId);
  
  return error ? 0 : (count || 0);
}

export async function getPostAuthor(authorId) {
  const { data } = await supabase
    .from('profiles')
    .select('name, email')
    .eq('id', authorId)
    .single();
  
  return data || { name: 'Anonymous', email: '?' };
}

// Reactions functions
export async function toggleReaction({ postId, userId, reactionType }) {
  // Check if user already has this reaction
  const { data: existing } = await supabase
    .from('reactions')
    .select('id')
    .eq('post_id', postId)
    .eq('user_id', userId)
    .eq('reaction_type', reactionType)
    .single();

  if (existing) {
    // Remove reaction
    await supabase
      .from('reactions')
      .delete()
      .eq('id', existing.id);
    return { ok: true, added: false };
  } else {
    // Add reaction
    const { error } = await supabase
      .from('reactions')
      .insert({ post_id: postId, user_id: userId, reaction_type: reactionType });
    
    if (error) return { ok: false, error: error.message };
    return { ok: true, added: true };
  }
}

export async function getPostReactions(postId) {
  const { data, error } = await supabase
    .from('reactions')
    .select('reaction_type, user_id', { count: 'exact' })
    .eq('post_id', postId);

  if (error) return {};

  // Count by reaction type
  const reactionCounts = {};
  const reactionTypes = ['👍', '❤️', '🔥', '🎉'];
  reactionTypes.forEach(type => {
    reactionCounts[type] = (data || []).filter(r => r.reaction_type === type).length;
  });

  return reactionCounts;
}

export async function getUserPostReactions(postId, userId) {
  const { data, error } = await supabase
    .from('reactions')
    .select('reaction_type')
    .eq('post_id', postId)
    .eq('user_id', userId);

  if (error) return [];

  return (data || []).map(r => r.reaction_type);
}

export async function getPostTotalReactions(postId) {
  const { count, error } = await supabase
    .from('reactions')
    .select('*', { count: 'exact', head: true })
    .eq('post_id', postId);

  return error ? 0 : (count || 0);
}
// Referral functions already implemented at profile creation in ensureProfile
// These functions are for updating/retrieving the code

export async function linkReferral(referrerId, referredId) {
  // Record referral and update referred_by
  const { error: refError } = await supabase
    .from('referrals')
    .insert({ referrer_id: referrerId, referred_id: referredId });
  
  if (refError) return { ok: false, error: refError.message };
  
  const { error: updateError } = await supabase
    .from('profiles')
    .update({ referred_by: referrerId })
    .eq('id', referredId);
  
  if (updateError) return { ok: false, error: updateError.message };
  return { ok: true };
}

export async function getReferralLeaderboard(limit = 10) {
  const { data, error } = await supabase
    .from('referrals')
    .select('referrer_id, profiles(name, email)', { count: 'exact' })
    .order('referrer_id');
  
  if (error) return [];
  
  // Group by referrer and count
  const leaderboard = {};
  (data || []).forEach((ref) => {
    if (!leaderboard[ref.referrer_id]) {
      leaderboard[ref.referrer_id] = {
        userId: ref.referrer_id,
        name: ref.profiles?.name || 'Anon',
        email: ref.profiles?.email,
        count: 0
      };
    }
    leaderboard[ref.referrer_id].count += 1;
  });
  
  return Object.values(leaderboard)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

export async function getReferralCode(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('referral_code')
    .eq('id', userId)
    .single();
  
  return error ? null : (data?.referral_code || null);
}

export async function generateReferralCode(userId) {
  // Generate a unique referral code if not already set
  const existing = await getReferralCode(userId);
  if (existing) return existing;
  
  const code = `REF${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
  const { data, error } = await supabase
    .from('profiles')
    .update({ referral_code: code })
    .eq('id', userId)
    .select('referral_code')
    .single();
  
  if (error) {
    console.error('Error generating referral code:', error);
    return null;
  }
  
  return data?.referral_code || null;
}

export async function checkIfAdmin(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('is_admin')
    .eq('id', userId)
    .single();
  
  return error ? false : (data?.is_admin || false);
}

// User stats functions
export async function getUserPostsCount(userId) {
  const { count, error } = await supabase
    .from('posts')
    .select('*', { count: 'exact', head: true })
    .eq('author_id', userId);
  
  return error ? 0 : (count || 0);
}

export async function getUserCommentsCount(userId) {
  const { count, error } = await supabase
    .from('comments')
    .select('*', { count: 'exact', head: true })
    .eq('author_id', userId);
  
  return error ? 0 : (count || 0);
}

export async function getUserReactionsCount(userId) {
  const { count, error } = await supabase
    .from('comment_reactions')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId);
  
  return error ? 0 : (count || 0);
}

// Moderation functions
export async function deletePost(postId) {
  const { error } = await supabase
    .from('posts')
    .delete()
    .eq('id', postId);
  
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export async function deleteComment(commentId) {
  const { error } = await supabase
    .from('comments')
    .delete()
    .eq('id', commentId);
  
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

// Hashtag functions
const ABUSIVE_WORDS = ['spam', 'abuse', 'hate', 'violence', 'porn', 'nsfw', 'adult'];

function sanitizeHashtag(tag) {
  // Remove special chars, convert to lowercase, limit to 50 chars
  return tag.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 50);
}

function containsAbusiveContent(tag) {
  const sanitized = tag.toLowerCase();
  return ABUSIVE_WORDS.some(word => sanitized.includes(word));
}

export function validateHashtags(tags) {
  if (!Array.isArray(tags)) return { ok: false, error: 'Tags must be an array' };
  if (tags.length > 3) return { ok: false, error: 'Maximum 3 hashtags allowed' };
  
  const sanitized = tags.map(t => sanitizeHashtag(String(t).trim())).filter(t => t.length > 0);
  
  for (const tag of sanitized) {
    if (containsAbusiveContent(tag)) {
      return { ok: false, error: `Hashtag "#${tag}" contains inappropriate content` };
    }
  }
  
  return { ok: true, tags: [...new Set(sanitized)] }; // Remove duplicates
}

export async function linkPostToHashtags(postId, hashtags) {
  if (!hashtags || hashtags.length === 0) return { ok: true };
  
  try {
    // Get or create hashtags
    const hashtagData = [];
    
    for (const tag of hashtags) {
      const sanitized = sanitizeHashtag(String(tag).trim());
      if (sanitized.length === 0) continue;
      
      // Try to get existing hashtag
      let { data: existing } = await supabase
        .from('hashtags')
        .select('id')
        .eq('tag', sanitized)
        .single();
      
      let hashtagId;
      if (!existing) {
        // Create new hashtag
        const { data: created, error: createError } = await supabase
          .from('hashtags')
          .insert({ tag: sanitized, usage_count: 1 })
          .select()
          .single();
        
        if (createError) continue;
        hashtagId = created.id;
      } else {
        hashtagId = existing.id;
        // Increment usage count
        await supabase
          .from('hashtags')
          .update({ usage_count: supabase.rpc('increment', { x: 1, table: 'hashtags', id: hashtagId }) })
          .eq('id', hashtagId);
      }
      
      if (hashtagId) {
        hashtagData.push({ post_id: postId, hashtag_id: hashtagId });
      }
    }
    
    if (hashtagData.length > 0) {
      const { error } = await supabase
        .from('post_hashtags')
        .insert(hashtagData);
      
      if (error) return { ok: false, error: error.message };
    }
    
    return { ok: true };
  } catch (error) {
    return { ok: false, error: error.message };
  }
}

export async function getTrendingHashtags(limit = 10) {
  const { data, error } = await supabase
    .from('hashtags')
    .select('id, tag, usage_count')
    .order('usage_count', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(limit);
  
  if (error) return [];
  return data || [];
}

export async function getPostsByHashtag(tag, limit = 20, offset = 0, currentUser = null) {
  // Get hashtag ID
  const { data: hashtagData } = await supabase
    .from('hashtags')
    .select('id')
    .eq('tag', sanitizeHashtag(tag))
    .single();
  
  if (!hashtagData) return { ok: true, posts: [] };
  
  // Get posts with this hashtag
  const { data: posts, error, count } = await supabase
    .from('post_hashtags')
    .select('posts(*)', { count: 'exact' })
    .eq('hashtag_id', hashtagData.id)
    .range(offset, offset + limit - 1);
  
  if (error) return { ok: false, posts: [], total: 0, error: error.message };
  
  // Extract posts and filter by visibility
  const postsList = posts?.map(ph => ph.posts).filter(post => {
    const visibility = post.visibility || 'public';
    if (visibility === 'public') return true;
    if (visibility === 'private' && currentUser) return true;
    return false;
  }) || [];
  
  return { ok: true, posts: postsList, total: count || 0 };
}

export async function getPostHashtags(postId) {
  const { data, error } = await supabase
    .from('post_hashtags')
    .select('hashtag_id, hashtags(id, tag)')
    .eq('post_id', postId);
  
  if (error) return [];
  return data?.map(ph => ({ id: ph.hashtag_id, tag: ph.hashtags?.tag })).filter(h => h.tag) || [];
}